import React, { useState, useRef, useCallback } from 'react'
import { useWorkspaceStore } from '../../store/workspace'
import { FolderPlus, Trash, Folder, ChevronRight, ChevronDown, FileText, FolderOpen, FolderClosed, FilePlus } from 'lucide-react'
import { DndProvider, useDrag, useDrop } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import '../../theme/file-explorer.css'

// 拖拽项类型
const ItemTypes = {
  DOCUMENT: 'document',
  FOLDER: 'folder'
}

interface DragItem {
  id: string
  type: string
}

// 自定义钩子：管理右键菜单
const useContextMenu = () => {
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    visible: boolean;
  }>({
    x: 0,
    y: 0,
    visible: false,
  });
  
  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      visible: true,
    });
  }, []);
  
  const closeContextMenu = useCallback(() => {
    setContextMenu(prev => ({ ...prev, visible: false }));
  }, []);
  
  return {
    contextMenu,
    handleContextMenu,
    closeContextMenu
  };
};

// 文件夹组件
interface FolderComponentProps {
  folderId: string
  level: number
  expandedFolders: Record<string, boolean>
  setExpandedFolders: React.Dispatch<React.SetStateAction<Record<string, boolean>>>
  isRoot?: boolean // 添加是否为根目录的标志
}

// 添加右键菜单组件
interface ContextMenuProps {
  x: number;
  y: number;
  onClose: () => void;
  options: {
    icon: React.ReactNode;
    onClick: () => void;
    title: string;
    danger?: boolean;
  }[];
}

const FileContextMenu: React.FC<ContextMenuProps> = ({ x, y, onClose, options }) => {
  const menuRef = useRef<HTMLDivElement>(null);
  
  // 点击菜单外部时关闭
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);
  
  return (
    <div 
      ref={menuRef}
      className="file-context-menu"
      style={{ 
        left: `${x}px`, 
        top: `${y}px`,
      }}
    >
      {options.map((option, index) => (
        <button 
          key={index}
          className="file-explorer-button-small"
          onClick={() => {
            option.onClick();
            onClose();
          }}
          title={option.title}
        >
          {React.isValidElement(option.icon) && 
            React.cloneElement(option.icon, { 
              className: `file-menu-icon ${option.danger ? 'danger' : ''}`
            } as React.HTMLAttributes<SVGElement>)
          }
        </button>
      ))}
    </div>
  );
};

// 自定义钩子：管理拖放逻辑
const useFolderDragDrop = (folderId: string, isRoot: boolean) => {
  // 设置文件夹为可拖放目标
  const [{ isOver, canDrop }, drop] = useDrop({
    accept: [ItemTypes.DOCUMENT, ItemTypes.FOLDER],
    drop: (item: DragItem) => {
      if (item.type === ItemTypes.DOCUMENT) {
        return { type: 'DOCUMENT_DROP', targetId: folderId };
      } else if (item.type === ItemTypes.FOLDER) {
        // 不允许将文件夹移动到自己内部
        if (item.id !== folderId) {
          return { type: 'FOLDER_DROP', targetId: folderId };
        }
      }
      return undefined;
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });
  
  // 设置文件夹为可拖动项（除了根文件夹）
  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.FOLDER,
    item: { id: folderId, type: ItemTypes.FOLDER },
    canDrag: !isRoot, // 根文件夹不可拖动
    end: (item, monitor) => {
      const dropResult = monitor.getDropResult() as { type: string; targetId: string } | null;
      if (item && dropResult) {
        // 派发自定义事件，让文件夹组件处理
        window.dispatchEvent(new CustomEvent('file-explorer-drop', {
          detail: {
            type: dropResult.type,
            itemId: item.id,
            targetId: dropResult.targetId
          }
        }));
      }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  
  // 统一处理ref
  const dragDropRef = useCallback((node: HTMLDivElement | null) => {
    if (!node) return;
    
    if (isRoot) {
      drop(node);
    } else {
      drag(drop(node));
    }
  }, [drag, drop, isRoot]);
  
  return {
    isDragging,
    isOver,
    canDrop,
    dragDropRef
  };
};

// 自定义钩子：管理文档拖放
const useDocumentDragDrop = (documentId: string) => {
  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.DOCUMENT,
    item: { id: documentId, type: ItemTypes.DOCUMENT },
    end: (item, monitor) => {
      const dropResult = monitor.getDropResult() as { type: string; targetId: string } | null;
      if (item && dropResult) {
        // 派发自定义事件，让文件夹组件处理
        window.dispatchEvent(new CustomEvent('file-explorer-drop', {
          detail: {
            type: dropResult.type,
            itemId: item.id,
            targetId: dropResult.targetId
          }
        }));
      }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  
  return {
    isDragging,
    dragRef: drag
  };
};

const FolderComponent: React.FC<FolderComponentProps> = ({ 
  folderId, 
  level,
  expandedFolders,
  setExpandedFolders,
  isRoot = false // 默认不是根目录
}) => {
  const { 
    folders, 
    documents, 
    activeDocumentId, 
    setActiveDocument, 
    deleteDocument,
    deleteFolder,
    createDocument,
    createFolder,
    moveDocumentToFolder,
    moveFolderToFolder
  } = useWorkspaceStore()

  // 使用自定义钩子管理右键菜单
  const { contextMenu, handleContextMenu, closeContextMenu } = useContextMenu();
  
  // 使用自定义钩子管理拖放
  const { isDragging: folderDragging, isOver, canDrop, dragDropRef } = useFolderDragDrop(folderId, isRoot);

  const folder = folders[folderId];
  const isExpanded = expandedFolders[folderId] || false;
  
  const toggleFolder = (e: React.MouseEvent) => {
    e.stopPropagation()
    setExpandedFolders(prev => ({
      ...prev,
      [folderId]: !prev[folderId]
    }))
  }
  
  const handleCreateDocument = () => {
    createDocument('新文档', folderId)
  }
  
  const handleCreateSubFolder = () => {
    const folderName = window.prompt('请输入文件夹名称')
    if (folderName) {
      try {
        createFolder(folderName, folderId);
      } catch (error) {
        console.error('Error creating folder:', error);
      }
    }
  }
  
  const handleDeleteDocument = (docId: string) => {
    if (window.confirm('确定要删除此文档吗？')) {
      deleteDocument(docId)
    }
  }
  
  const handleDeleteFolder = () => {
    // 不允许删除根文件夹
    if (isRoot) return;
    
    // 检查文件夹是否为空
    const hasChildren = folder.children && folder.children.length > 0;
    const hasDocuments = folder.documents && folder.documents.length > 0;
    
    if (hasChildren || hasDocuments) {
      if (!window.confirm('此文件夹不为空，确定要删除此文件夹及其所有内容吗？')) {
        return;
      }
    } else {
      if (!window.confirm('确定要删除此文件夹吗？')) {
        return;
      }
    }
    
    deleteFolder(folderId);
  }
  
  // 监听拖放完成事件
  React.useEffect(() => {
    const handleDrop = (e: CustomEvent) => {
      if (!e.detail) return;
      
      const { type, itemId, targetId } = e.detail;
      if (targetId !== folderId) return;
      
      if (type === 'DOCUMENT_DROP') {
        moveDocumentToFolder(itemId, targetId);
      } else if (type === 'FOLDER_DROP') {
        moveFolderToFolder(itemId, targetId);
      }
    };
    
    window.addEventListener('file-explorer-drop', handleDrop as EventListener);
    return () => {
      window.removeEventListener('file-explorer-drop', handleDrop as EventListener);
    };
  }, [folderId, moveDocumentToFolder, moveFolderToFolder]);
  
  // 右键菜单选项
  const contextMenuOptions = [
    {
      icon: <FilePlus />,
      onClick: handleCreateDocument,
      title: '新建文档',
    },
    {
      icon: <FolderPlus />,
      onClick: handleCreateSubFolder,
      title: '新建子文件夹',
    },
    ...(isRoot ? [] : [
      {
        icon: <Trash />,
        onClick: handleDeleteFolder,
        title: '删除文件夹',
        danger: true,
      },
    ]),
  ];
  
  // 计算样式
  const opacity = folderDragging ? 0.4 : 1;
  const backgroundColor = isOver && canDrop ? '#3a3a3a' : 'transparent';
  const paddingLeft = `${level * 0.75}rem`;
  
  return (
    <div style={{ opacity }}>
      <div 
        ref={dragDropRef}
        className={`file-tree-item group ${isRoot ? 'root-folder' : ''}`}
        onClick={toggleFolder}
        onContextMenu={handleContextMenu}
        style={{ backgroundColor, paddingLeft }}
      >
        <button 
          className="mr-1 flex items-center justify-center"
          onClick={toggleFolder}
        >
          {isExpanded ? 
            <ChevronDown className="w-3.5 h-3.5 chevron-icon" /> : 
            <ChevronRight className="w-3.5 h-3.5 chevron-icon" />
          }
        </button>
        
        {isExpanded ? 
          <FolderOpen className="w-3.5 h-3.5 mr-1.5 folder-icon" /> : 
          <Folder className="w-3.5 h-3.5 mr-1.5 folder-icon" />
        }
        
        <span className="truncate flex-1">{folder.name}</span>
      </div>
      
      {contextMenu.visible && (
        <FileContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={closeContextMenu}
          options={contextMenuOptions}
        />
      )}
      
      {isExpanded && (
        <div className="ml-2">
          {/* 先显示子文件夹 */}
          {folder.children && folder.children.map(childId => (
            <FolderComponent 
              key={childId}
              folderId={childId}
              level={level + 1}
              expandedFolders={expandedFolders}
              setExpandedFolders={setExpandedFolders}
            />
          ))}
          
          {/* 然后显示文档 */}
          {folder.documents && folder.documents.map(docId => {
            const doc = documents[docId]
            if (!doc) return null
            
            return (
              <DocumentItem 
                key={docId} 
                document={doc} 
                isActive={activeDocumentId === docId}
                onActivate={() => setActiveDocument(docId)}
                onDelete={() => handleDeleteDocument(docId)}
                level={level + 1}
              />
            )
          })}
        </div>
      )}
    </div>
  )
}

// 文档项组件
interface DocumentItemProps {
  document: {
    id: string
    title: string
  }
  isActive: boolean
  onActivate: () => void
  onDelete: (e: React.MouseEvent) => void
  level: number
}

const DocumentItem: React.FC<DocumentItemProps> = ({ 
  document, 
  isActive, 
  onActivate, 
  onDelete,
  level 
}) => {
  // 使用自定义钩子管理右键菜单
  const { contextMenu, handleContextMenu, closeContextMenu } = useContextMenu();
  
  // 使用自定义钩子管理拖放
  const { isDragging, dragRef } = useDocumentDragDrop(document.id);
  
  // 右键菜单选项
  const contextMenuOptions = [
    {
      icon: <Trash />,
      onClick: () => {
        const mockEvent = { 
          preventDefault: () => {}, 
          stopPropagation: () => {} 
        };
        onDelete(mockEvent as React.MouseEvent<HTMLElement>);
      },
      title: '删除文档',
      danger: true,
    },
  ];
  
  const opacity = isDragging ? 0.4 : 1;
  const paddingLeft = `${level * 0.75}rem`;
  
  // 创建引用回调函数
  const setNodeRef = useCallback((node: HTMLDivElement | null) => {
    dragRef(node);
  }, [dragRef]);
  
  return (
    <>
      <div 
        ref={setNodeRef}
        className={`file-tree-item group ${isActive ? 'active' : ''}`}
        onClick={(e) => {
          e.stopPropagation()
          onActivate()
        }}
        onContextMenu={handleContextMenu}
        style={{ opacity, paddingLeft }}
      >
        <FileText className="w-3.5 h-3.5 mr-1.5 file-icon" />
        <span className={`truncate flex-1 file-name ${isActive ? 'active' : ''}`}>{document.title}</span>
      </div>
      
      {contextMenu.visible && (
        <FileContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={closeContextMenu}
          options={contextMenuOptions}
        />
      )}
    </>
  )
}

const FileExplorer: React.FC = () => {
  const { folders, createFolder, createDocument } = useWorkspaceStore()
  const [expandedFolders, setExpandedFolders] = useState<Record<string, boolean>>({
    root: true // 默认展开根目录
  })
  
  const handleCreateFolder = () => {
    const folderName = window.prompt('请输入文件夹名称')
    if (folderName) {
      try {
        createFolder(folderName);
      } catch (error) {
        console.error('Error creating folder:', error);
      }
    }
  }
  
  // 检查是否有任何文件夹处于展开状态（除了根文件夹）
  const hasExpandedFolders = Object.entries(expandedFolders).some(
    ([folderId, isExpanded]) => folderId !== 'root' && isExpanded
  );
  
  // 管理文件夹展开状态的函数
  const toggleAllFolders = useCallback(() => {
    if (hasExpandedFolders) {
      // 收起所有文件夹，只保留根文件夹展开
      setExpandedFolders({
        root: true
      });
    } else {
      // 展开所有文件夹
      const allFolders: Record<string, boolean> = { root: true };
      Object.keys(folders).forEach(folderId => {
        allFolders[folderId] = true;
      });
      setExpandedFolders(allFolders);
    }
  }, [hasExpandedFolders, folders]);
  
  return (
    <DndProvider backend={HTML5Backend}>
      <div className="h-full flex flex-col">
        {/* 顶部按钮栏 */}
        <div className="flex justify-center items-center space-x-2 py-1">
          <button
            className="file-explorer-button"
            onClick={() => createDocument('新文档')}
            title="新建文档"
          >
            <FilePlus className="w-4 h-4 file-icon" />
          </button>
          
          <button
            className="file-explorer-button"
            onClick={handleCreateFolder}
            title="新建文件夹"
          >
            <FolderPlus className="w-4 h-4 folder-icon" />
          </button>
          
          {/* 根据文件夹展开状态显示展开或收缩按钮 */}
          <button
            className="file-explorer-button"
            onClick={toggleAllFolders}
            title={hasExpandedFolders ? "收起所有文件夹" : "展开所有文件夹"}
          >
            {hasExpandedFolders ? (
              <FolderClosed className="w-4 h-4 folder-icon" />
            ) : (
              <FolderOpen className="w-4 h-4 folder-icon" />
            )}
          </button>
        </div>
        
        {/* 文件夹内容区域 */}
        <div className="flex-1 overflow-auto">
          <FolderComponent 
            folderId="root"
            level={0}
            expandedFolders={expandedFolders}
            setExpandedFolders={setExpandedFolders}
            isRoot={true}
          />
        </div>
      </div>
    </DndProvider>
  )
}

export default FileExplorer 